import { AnimatePresence, motion } from 'framer-motion'
import React, { useEffect, useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import AvatarStack from 'src/components/AvatarStack'
import { UserProfile } from 'src/types/user'

import { mainContentVariants, ANIMATION } from './geometry'
import type { VisibleCard } from './types'
import ThumbnailSvg from '../../../assets/homepage/ThumbnailSvg.png'
import ImageWithFallback from '../../../components/ImageWithFallback/ImageWithFallback'

const TOOLTIP_OFFSET = { x: -65, y: 16 }

const CARD_IMAGE_WIDTH = 560
const CARD_IMAGE_HEIGHT = 320
const BLUR_LAYER_HEIGHT = 125
const BLUR_LAYER_BOTTOM_OFFSET = 25

function computeBlurBgPosition(imgWidth: number, imgHeight: number): string {
  const scale = Math.max(CARD_IMAGE_WIDTH / imgWidth, CARD_IMAGE_HEIGHT / imgHeight)
  const scaledHeight = imgHeight * scale
  const topCrop = (scaledHeight - CARD_IMAGE_HEIGHT) / 2
  const pixelOffsetFromTop = CARD_IMAGE_HEIGHT - BLUR_LAYER_HEIGHT + topCrop + BLUR_LAYER_BOTTOM_OFFSET
  return `-${pixelOffsetFromTop}px`
}

export default function IdeaCard({
  card,
  isMain,
  isSide,
  userData,
}: {
  card: VisibleCard
  isMain: boolean
  isSide: boolean
  userData?: UserProfile
}) {
  const [blurBgPositionY, setBlurBgPositionY] = useState<string>(`-${CARD_IMAGE_HEIGHT - BLUR_LAYER_HEIGHT}px`)
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const [showBrandTooltip, setShowBrandTooltip] = useState(false)
  const [brandTooltipPos, setBrandTooltipPos] = useState({ x: 0, y: 0 })

  // Compute blur background position on image load
  useEffect(() => {
    if (!card.coverImageUrl) return
    const img = new Image()
    img.onload = () => {
      const posY = computeBlurBgPosition(img.naturalWidth, img.naturalHeight)
      setBlurBgPositionY(posY)
    }
    img.src = card.coverImageUrl
  }, [card.coverImageUrl])

  // Calculate visible brands and remaining count
  const visibleBrands = useMemo(() => card.brands.slice(0, 3), [card.brands])
  const remaining = useMemo(() => card.brands.length - 3, [card.brands])

  const handleBrandEnter = (e: React.MouseEvent) => {
    if (remaining <= 0) return
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    setBrandTooltipPos({ x: rect.left, y: rect.top })
    setShowBrandTooltip(true)
  }

  const blurLayerStyle = {
    backgroundImage: `url(${card.coverImageUrl})`,
    backgroundPosition: `center ${blurBgPositionY}`,
  }

  return (
    <div className="group relative h-full w-full">
      {/* Background image */}
      <div
        className={[
          'idea-card__border-ring absolute inset-0 transition-opacity duration-200 ease-in overflow-hidden rounded-3xl',
          isSide ? 'opacity-50 group-hover:opacity-70' : 'opacity-100',
        ].join(' ')}
      >
        <ImageWithFallback
          src={card.coverImageUrl}
          fallbackSrc={ThumbnailSvg}
          alt={`${card.title} cover`}
          className="h-full w-full object-cover"
          style={{
            objectFit: 'cover',
            objectPosition: 'center',
          }}
          onLoad={() => setIsImageLoaded(true)}
        />
      </div>

      {/* Image loading placeholder */}
      {!isImageLoaded && <div className="absolute inset-0 rounded-3xl z-10 image-placeholder" />}

      {/* Hover overlay for main card */}
      {isMain && (
        <div
          className={[
            'absolute inset-0 rounded-3xl transition-all duration-300',
            'bg-black/0 group-hover:bg-black/8',
          ].join(' ')}
        />
      )}

      {/* Main card content (visible on main card only) */}
      <AnimatePresence>
        {isMain && isImageLoaded && (
          <>
            {/* Blur layer at bottom */}
            <motion.div
              key="blur-layer"
              className="blur-layer absolute left-0 w-full pointer-events-none z-10 bg-cover bg-no-repeat"
              style={{ ...blurLayerStyle, bottom: '-25px' }}
              animate={{
                opacity: isMain ? 1 : 0.3,
                filter: isMain ? 'blur(15px)' : 'blur(0px)',
              }}
              transition={{ duration: 0.2, ease: ANIMATION.EASING }}
            />

            {/* Gradient overlay */}
            <motion.div
              key="gradient"
              className="gradient-overlay absolute bottom-0 left-0 w-full h-[120px] pointer-events-none z-20"
              animate={{ opacity: isMain ? 1 : 0 }}
              transition={{ duration: 0.2, ease: ANIMATION.EASING }}
            />

            {/* Content area - Brand badges and title */}
            <motion.div
              key="content"
              className="absolute inset-0 z-30 flex flex-col justify-between p-6 rounded-3xl"
              variants={mainContentVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              {/* Brands section - top */}
              <div className="flex items-center justify-between">
                <div className="flex">
                  {visibleBrands.map(
                    brand =>
                      brand.logoUrl &&
                      brand.name !== 'Non-branded' && (
                        <div
                          key={brand.id}
                          className="brand w-14 h-14 rounded-full overflow-hidden border-2 border-white bg-white [&:not(:first-child)]:-ml-5"
                          title={brand.name}
                        >
                          {brand.logoUrl && brand.name !== 'Non-branded' && (
                            <img src={brand.logoUrl} alt={brand.name} className="w-full h-full object-cover" />
                          )}
                        </div>
                      ),
                  )}

                  {remaining > 0 && (
                    <div
                      className="brand brand--more w-14 h-14 border-2 border-white rounded-full overflow-hidden bg-[#f1f1f1] -ml-5 flex items-center justify-center text-xs font-bold"
                      onMouseEnter={handleBrandEnter}
                      onMouseLeave={() => setShowBrandTooltip(false)}
                      role="button"
                      tabIndex={0}
                    >
                      +{remaining}
                    </div>
                  )}
                </div>
                <div className="idea-card__avatar w-14 h-14 rounded-full border-2 border-white bg-[#fafafa] flex items-center justify-center relative">
                  <AvatarStack
                    firstName={userData?.firstName}
                    lastName={userData?.lastName}
                    avatarUrl={userData?.avatarUrl}
                    ideacard={true}
                  />
                </div>
              </div>

              {/* Title section - bottom */}
              <div>
                <h3 className="relative text-white text-xl leading-7 w-[516px] line-clamp-2 font-bold">{card.title}</h3>
              </div>
            </motion.div>

            {/* Brand tooltip */}
            {showBrandTooltip &&
              remaining > 0 &&
              createPortal(
                <div
                  className="tooltip fixed bg-white text-black shadow-lg text-xs leading-5 font-medium py-1.5 px-2.5 rounded-md pointer-events-none z-[9999]"
                  style={{
                    left: brandTooltipPos.x + TOOLTIP_OFFSET.x,
                    top: brandTooltipPos.y + TOOLTIP_OFFSET.y,
                    transform: 'translateY(-100%)',
                    maxWidth: '180px',
                    whiteSpace: 'normal',
                  }}
                >
                  {card.brands.map(b => b.name).join(', ')}
                </div>,
                document.body,
              )}
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
